/**
 * DeployToFunnelModal
 *
 * Modal for deploying bulk-generated paid content to a funnel structure.
 * Allows platform selection, funnel structure preview, budget configuration,
 * and creates a complete funnel template for the Funnel Builder.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
    X,
    Layers,
    ChevronDown,
    ChevronRight,
    Image,
    Video,
    LayoutGrid,
    DollarSign,
    Calendar,
    ArrowRight,
    AlertCircle,
    CheckCircle,
    FlaskConical,
    RefreshCw
} from 'lucide-react';
import {
    buildFunnelFromBriefs,
    buildFunnelTemplate,
    getFunnelPreviewStats
} from '../../services/funnelBuildService';
import PlacementConfigSection from './PlacementConfigSection';
import CTAConfigSection from './CTAConfigSection';
import { getUniqueRequiredAspectRatios } from '../../services/bulkGenerationService';

// ============================================================================
// CONSTANTS
// ============================================================================

const AD_PLATFORMS = [
    { id: 'meta', label: 'Meta Ads', color: '#1877F2', description: 'Facebook & Instagram' },
    { id: 'google', label: 'Google Ads', color: '#4285F4', description: 'Search & Display' },
    { id: 'tiktok', label: 'TikTok Ads', color: '#000000', description: 'TikTok For Business' },
    { id: 'linkedin', label: 'LinkedIn Ads', color: '#0A66C2', description: 'LinkedIn Campaign Manager' }
];

const STAGE_COLORS = {
    TOFU: 'var(--color-info)',
    MOFU: 'var(--color-warning)',
    BOFU: 'var(--color-success)'
};

const STAGE_LABELS = {
    TOFU: 'Awareness',
    MOFU: 'Consideration',
    BOFU: 'Conversion'
};

const CONTENT_TYPE_ICONS = {
    image: Image,
    video: Video,
    carousel: LayoutGrid
};

const DEFAULT_BUDGETS = {
    TOFU: 40,
    MOFU: 35,
    BOFU: 25
};

// ============================================================================
// COMPONENT
// ============================================================================

const DeployToFunnelModal = ({
    isOpen,
    onClose,
    briefs = [],
    scheduledPosts = [],
    onComplete,
    brand = null  // Brand object for default URL, UTM settings, etc.
}) => {
    const [selectedPlatform, setSelectedPlatform] = useState('meta');
    const [budgets, setBudgets] = useState({ ...DEFAULT_BUDGETS });
    const [expandedStages, setExpandedStages] = useState({ TOFU: true, MOFU: true, BOFU: true });

    // Phase 6: Placement and CTA configuration
    const [selectedPlacements, setSelectedPlacements] = useState([]);
    const [placementValidation, setPlacementValidation] = useState({ valid: true });
    const [ctaConfigs, setCtaConfigs] = useState({
        TOFU: { cta: 'LEARN_MORE', destinationType: 'url', url: '' },
        MOFU: { cta: 'SIGN_UP', destinationType: 'url', url: '' },
        BOFU: { cta: 'SHOP_NOW', destinationType: 'product', url: '' }
    });

    // Preview stats for the selected platform
    const previewStats = useMemo(() => {
        return getFunnelPreviewStats(briefs, selectedPlatform);
    }, [briefs, selectedPlatform]);

    // Get available aspect ratios from briefs
    const availableAspectRatios = useMemo(() => {
        const platforms = briefs
            .filter(b => b.contentType === 'paid')
            .flatMap(b => b.platforms || []);
        const uniquePlatforms = [...new Set(platforms)];

        if (uniquePlatforms.length === 0) return ['1:1'];

        // Get ratios based on generated assets
        const ratios = new Set();
        briefs.forEach(brief => {
            if (brief.generatedAssets) {
                brief.generatedAssets.forEach(asset => {
                    if (asset.aspectRatio) ratios.add(asset.aspectRatio);
                });
            }
        });

        // Fallback to platform defaults if no assets yet
        if (ratios.size === 0) {
            return getUniqueRequiredAspectRatios(uniquePlatforms);
        }

        return Array.from(ratios);
    }, [briefs]);

    // Build the full funnel structure for preview
    const funnelStructure = useMemo(() => {
        if (!previewStats.hasContent) return null;
        return buildFunnelFromBriefs(briefs, scheduledPosts, selectedPlatform, {
            budgetPerStage: budgets,
            placements: selectedPlacements,
            ctaConfigs: ctaConfigs
        });
    }, [briefs, scheduledPosts, selectedPlatform, budgets, previewStats.hasContent, selectedPlacements, ctaConfigs]);

    const handleBudgetChange = useCallback((stage, value) => {
        const numValue = parseInt(value) || 0;
        setBudgets(prev => ({ ...prev, [stage]: numValue }));
    }, []);

    const handleCtaConfigChange = useCallback((stage, config) => {
        setCtaConfigs(prev => ({ ...prev, [stage]: config }));
    }, []);

    const toggleStage = useCallback((stage) => {
        setExpandedStages(prev => ({ ...prev, [stage]: !prev[stage] }));
    }, []);

    const handleCreate = useCallback(() => {
        if (!funnelStructure) return;

        // Check placement validation
        if (!placementValidation.valid) {
            console.warn('Deployment attempted with invalid placements:', placementValidation.missing);
        }

        const template = buildFunnelTemplate(funnelStructure);

        // Pass additional config to onComplete
        onComplete(template, selectedPlatform, funnelStructure.pillarMapping, {
            placements: selectedPlacements,
            ctaConfigs: ctaConfigs,
            budgets: budgets
        });
    }, [funnelStructure, selectedPlatform, onComplete, placementValidation, selectedPlacements, ctaConfigs, budgets]);

    const totalBudget = Object.values(budgets).reduce((sum, v) => sum + v, 0);

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: 'var(--space-4)'
            }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                className="card"
                style={{
                    width: '100%',
                    maxWidth: '800px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <div style={{
                            width: 40,
                            height: 40,
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--color-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Layers size={20} style={{ color: 'var(--color-primary-foreground)' }} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>Deploy to Funnel</h3>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Create an ad funnel from your generated content
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-2)',
                            color: 'var(--color-text-secondary)',
                            borderRadius: 'var(--radius-sm)'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 'var(--space-4)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-4)'
                }}>
                    {/* Content Summary Banner */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-3)',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <CheckCircle size={14} style={{ color: 'var(--color-success)' }} />
                            <span><strong>{previewStats.compatibleBriefs}</strong> paid ads to deploy</span>
                        </div>
                        {previewStats.organicBriefs > 0 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                <Calendar size={14} />
                                <span>{previewStats.organicBriefs} organic posts remain in calendar</span>
                            </div>
                        )}
                    </div>

                    {/* Platform Selection */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            marginBottom: 'var(--space-2)',
                            color: 'var(--color-text-primary)'
                        }}>
                            Ad Platform
                        </label>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 'var(--space-2)'
                        }}>
                            {AD_PLATFORMS.map(platform => {
                                const stats = getFunnelPreviewStats(briefs, platform.id);
                                const isSelected = selectedPlatform === platform.id;
                                return (
                                    <button
                                        key={platform.id}
                                        onClick={() => setSelectedPlatform(platform.id)}
                                        style={{
                                            padding: 'var(--space-3)',
                                            border: `2px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                            borderRadius: 'var(--radius-md)',
                                            background: isSelected ? 'var(--color-primary-light, rgba(99, 102, 241, 0.08))' : 'var(--color-surface)',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <div style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            background: platform.color,
                                            margin: '0 auto var(--space-2)'
                                        }} />
                                        <div style={{
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 600,
                                            color: 'var(--color-text-primary)'
                                        }}>
                                            {platform.label}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)',
                                            marginTop: 2
                                        }}>
                                            {stats.compatibleBriefs} ads
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Funnel Structure Preview */}
                    {!previewStats.hasContent ? (
                        <div style={{
                            padding: 'var(--space-6)',
                            textAlign: 'center',
                            color: 'var(--color-text-secondary)',
                            background: 'var(--color-bg-tertiary)',
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <AlertCircle size={24} style={{ marginBottom: 'var(--space-2)', opacity: 0.5 }} />
                            <p style={{ margin: 0 }}>No compatible paid ads for this platform</p>
                        </div>
                    ) : funnelStructure && (
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-text-primary)'
                            }}>
                                Funnel Structure
                            </label>
                            <div style={{
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                overflow: 'hidden'
                            }}>
                                {funnelStructure.campaigns.map((campaign, campaignIdx) => {
                                    const stage = campaign._stage;
                                    const isExpanded = expandedStages[stage];
                                    const campaignAdsets = funnelStructure.adsets.filter(a => a._campaignIndex === campaignIdx);
                                    const hasRetargetingPool = funnelStructure.retargetingPools.some(p => p._sourceStage === stage);

                                    return (
                                        <div key={campaignIdx}>
                                            {/* Campaign Header */}
                                            <div
                                                onClick={() => toggleStage(stage)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: 'var(--space-3) var(--space-4)',
                                                    background: 'var(--color-bg-tertiary)',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid var(--color-border)',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        padding: '2px 8px',
                                                        borderRadius: 'var(--radius-sm)',
                                                        background: STAGE_COLORS[stage],
                                                        color: 'white',
                                                        fontSize: 'var(--font-size-xs)',
                                                        fontWeight: 700
                                                    }}>
                                                        {stage}
                                                    </span>
                                                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                                                        {campaign.data.name}
                                                    </span>
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-3)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-secondary)'
                                                }}>
                                                    {campaign.data.schedule?.start && (
                                                        <span>{campaign.data.schedule.start} - {campaign.data.schedule.end}</span>
                                                    )}
                                                    <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
                                                        ${budgets[stage]}/day
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Expanded Content */}
                                            {isExpanded && (
                                                <div style={{ padding: 'var(--space-2) var(--space-4) var(--space-3)' }}>
                                                    {campaignAdsets.map((adset, adsetIdx) => {
                                                        const globalAdsetIdx = funnelStructure.adsets.indexOf(adset);
                                                        const adsetAds = funnelStructure.ads.filter(a =>
                                                            a._adsetIndex === globalAdsetIdx && a._abtestIndex === undefined
                                                        );
                                                        const adsetAbtests = funnelStructure.abtests.filter(a => a._adsetIndex === globalAdsetIdx);

                                                        return (
                                                            <div key={adsetIdx} style={{
                                                                marginTop: 'var(--space-2)',
                                                                marginLeft: 'var(--space-4)',
                                                                paddingLeft: 'var(--space-3)',
                                                                borderLeft: '2px solid var(--color-border)'
                                                            }}>
                                                                {/* AdSet */}
                                                                <div style={{
                                                                    fontSize: 'var(--font-size-sm)',
                                                                    fontWeight: 600,
                                                                    color: 'var(--color-text-primary)',
                                                                    marginBottom: 'var(--space-2)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 'var(--space-2)'
                                                                }}>
                                                                    <LayoutGrid size={14} style={{ color: 'var(--color-text-secondary)' }} />
                                                                    {adset.data.name}
                                                                    <span style={{
                                                                        fontSize: 'var(--font-size-xs)',
                                                                        color: 'var(--color-text-secondary)',
                                                                        fontWeight: 400
                                                                    }}>
                                                                        ({adsetAds.length + adsetAbtests.reduce((sum, t) => {
                                                                            const abtestIdx = funnelStructure.abtests.indexOf(t);
                                                                            return sum + funnelStructure.ads.filter(a => a._abtestIndex === abtestIdx).length;
                                                                        }, 0)} ads)
                                                                    </span>
                                                                </div>

                                                                {/* Regular Ads */}
                                                                {adsetAds.map((ad, adIdx) => (
                                                                    <AdPreviewRow key={`ad-${adIdx}`} ad={ad} />
                                                                ))}

                                                                {/* A/B Tests */}
                                                                {adsetAbtests.map((abtest, abtestIdx) => {
                                                                    const globalAbtestIdx = funnelStructure.abtests.indexOf(abtest);
                                                                    const variantAds = funnelStructure.ads.filter(a => a._abtestIndex === globalAbtestIdx);

                                                                    return (
                                                                        <div key={`abtest-${abtestIdx}`} style={{
                                                                            marginLeft: 'var(--space-3)',
                                                                            marginTop: 'var(--space-2)',
                                                                            padding: 'var(--space-2)',
                                                                            background: 'var(--color-bg-tertiary)',
                                                                            borderRadius: 'var(--radius-sm)',
                                                                            border: '1px dashed var(--color-border)'
                                                                        }}>
                                                                            <div style={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 'var(--space-2)',
                                                                                fontSize: 'var(--font-size-xs)',
                                                                                fontWeight: 600,
                                                                                color: 'var(--color-warning)',
                                                                                marginBottom: 'var(--space-2)'
                                                                            }}>
                                                                                <FlaskConical size={12} />
                                                                                {abtest.data.name}
                                                                            </div>
                                                                            {variantAds.map((ad, vIdx) => (
                                                                                <AdPreviewRow key={`variant-${vIdx}`} ad={ad} />
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })}

                                                    {/* Retargeting Pool indicator */}
                                                    {hasRetargetingPool && (
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 'var(--space-2)',
                                                            marginTop: 'var(--space-3)',
                                                            marginLeft: 'var(--space-4)',
                                                            padding: 'var(--space-2) var(--space-3)',
                                                            background: 'rgba(234, 179, 8, 0.08)',
                                                            borderRadius: 'var(--radius-sm)',
                                                            fontSize: 'var(--font-size-xs)',
                                                            color: 'var(--color-warning)'
                                                        }}>
                                                            <RefreshCw size={12} />
                                                            <span>Retargeting pool feeds into next stage</span>
                                                            <ArrowRight size={12} />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Budget Configuration */}
                    {previewStats.hasContent && (
                        <div>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-text-primary)'
                            }}>
                                <DollarSign size={14} />
                                Daily Budget per Stage
                            </label>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 'var(--space-3)'
                            }}>
                                {funnelStructure?.stats.stages.map(stage => (
                                    <div key={stage} style={{
                                        padding: 'var(--space-3)',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: 'var(--radius-md)',
                                        background: 'var(--color-surface)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            <span style={{
                                                display: 'inline-flex',
                                                padding: '1px 6px',
                                                borderRadius: 'var(--radius-sm)',
                                                background: STAGE_COLORS[stage],
                                                color: 'white',
                                                fontSize: '10px',
                                                fontWeight: 700
                                            }}>
                                                {stage}
                                            </span>
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-secondary)'
                                            }}>
                                                {STAGE_LABELS[stage]}
                                            </span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-1)'
                                        }}>
                                            <span style={{
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-text-secondary)'
                                            }}>$</span>
                                            <input
                                                type="number"
                                                value={budgets[stage]}
                                                onChange={(e) => handleBudgetChange(stage, e.target.value)}
                                                min={0}
                                                style={{
                                                    width: '100%',
                                                    padding: 'var(--space-2)',
                                                    border: '1px solid var(--color-border)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    background: 'var(--color-bg-primary)',
                                                    color: 'var(--color-text-primary)',
                                                    fontSize: 'var(--font-size-sm)',
                                                    outline: 'none'
                                                }}
                                            />
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-secondary)',
                                                whiteSpace: 'nowrap'
                                            }}>/day</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div style={{
                                marginTop: 'var(--space-2)',
                                textAlign: 'right',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Total: <strong style={{ color: 'var(--color-primary)' }}>${totalBudget}/day</strong>
                            </div>
                        </div>
                    )}

                    {/* Phase 6: Placement Configuration */}
                    {previewStats.hasContent && (
                        <PlacementConfigSection
                            platform={selectedPlatform}
                            availableAspectRatios={availableAspectRatios}
                            selectedPlacements={selectedPlacements}
                            onPlacementChange={setSelectedPlacements}
                            autoAssign={true}
                            onValidationChange={setPlacementValidation}
                        />
                    )}

                    {/* Phase 6: CTA Configuration per Stage */}
                    {previewStats.hasContent && funnelStructure?.stats.stages.map(stage => {
                        // Find linked product for this stage if any
                        const stageBriefs = briefs.filter(b =>
                            b.funnelStage === stage && b.contentType === 'paid'
                        );
                        const linkedProduct = stageBriefs.find(b => b.linkedProduct)?.linkedProduct || null;

                        return (
                            <CTAConfigSection
                                key={stage}
                                funnelStage={stage}
                                ctaConfig={ctaConfigs[stage] || {}}
                                onConfigChange={(config) => handleCtaConfigChange(stage, config)}
                                linkedProduct={linkedProduct}
                                availableLeadForms={[]}  // TODO: Fetch from Meta API
                                platform={selectedPlatform}
                                brandDefaultUrl={brand?.defaultLandingPage}
                                utmEnabled={brand?.utmSettings?.enabled || false}
                            />
                        );
                    })}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-bg-tertiary)'
                }}>
                    <div style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        {funnelStructure && (
                            <span>
                                {funnelStructure.stats.campaignCount} campaigns,{' '}
                                {funnelStructure.stats.adsetCount} ad sets,{' '}
                                {funnelStructure.stats.adCount} ads
                                {funnelStructure.stats.abtestCount > 0 && `, ${funnelStructure.stats.abtestCount} A/B tests`}
                                {funnelStructure.stats.retargetingPoolCount > 0 && `, ${funnelStructure.stats.retargetingPoolCount} retargeting pools`}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleCreate}
                            disabled={!previewStats.hasContent}
                        >
                            <Layers size={16} />
                            Create Funnel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Single ad preview row in the funnel tree
 */
const AdPreviewRow = ({ ad }) => {
    const ContentIcon = CONTENT_TYPE_ICONS[ad.data.outputType] || Image;

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginLeft: 'var(--space-3)',
            padding: 'var(--space-1) var(--space-2)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-primary)'
        }}>
            {/* Thumbnail */}
            {ad.data.creativeThumbnail || ad.data.creativeUrl ? (
                <img
                    src={ad.data.creativeThumbnail || ad.data.creativeUrl}
                    alt=""
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: 'var(--radius-sm)',
                        objectFit: 'cover',
                        border: '1px solid var(--color-border)'
                    }}
                />
            ) : (
                <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-bg-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <ContentIcon size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                </div>
            )}

            {/* Ad info */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {ad.data.variant && (
                        <span style={{
                            fontWeight: 700,
                            color: 'var(--color-warning)',
                            marginRight: 'var(--space-1)'
                        }}>
                            {ad.data.variant}
                        </span>
                    )}
                    {ad.data.name}
                </div>
            </div>

            {/* Content type badge */}
            <span style={{
                padding: '1px 6px',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-bg-tertiary)',
                fontSize: '10px',
                color: 'var(--color-text-secondary)',
                textTransform: 'capitalize'
            }}>
                {ad.data.outputType || 'image'}
            </span>

            {/* Scheduled date */}
            {ad.data.scheduledDate && (
                <span style={{
                    fontSize: '10px',
                    color: 'var(--color-text-tertiary)',
                    whiteSpace: 'nowrap'
                }}>
                    {new Date(ad.data.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {ad.data.scheduledTime && ` ${ad.data.scheduledTime}`}
                </span>
            )}
        </div>
    );
};

export default DeployToFunnelModal;
